import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Gets the user's timezone preference
 * @returns The user's timezone or browser's timezone if not set
 */
export function getUserTimezone(): string {
  // First try to get from localStorage
  const saved = localStorage.getItem("timezone");
  if (saved) {
    return saved;
  }
  
  // Default to browser's timezone
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Sets the user's timezone preference in localStorage
 * @param timezone The timezone to set
 */
export function setUserTimezone(timezone: string): void {
  localStorage.setItem("timezone", timezone);
}

/**
 * Formats a date string or Date object according to the user's timezone preference
 * @param date The date to format
 * @param format The format string (dayjs format)
 * @param excludeTimezone If true, will not convert to user's timezone (for dates like DOB)
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date | dayjs.Dayjs,
  format: string = "MM/DD/YYYY",
  excludeTimezone: boolean = false,
): string {
  if (!date) {
    return "";
  }

  let dateObj = dayjs(date);

  if (!excludeTimezone) {
    const userTimezone = getUserTimezone();
    dateObj = dateObj.tz(userTimezone);
  }

  return dateObj.format(format);
}

/**
 * Formats a date string or Date object with time according to the user's timezone preference
 * @param date The date to format
 * @param format The format string (dayjs format)
 * @returns Formatted date string with time
 */
export function formatDateTime(
  date: string | Date | dayjs.Dayjs,
  format: string = "MM/DD/YYYY hh:mm A",
): string {
  return formatDate(date, format);
}

/**
 * Formats a date of birth (does not apply timezone conversion)
 * @param date The date to format
 * @param format The format string (dayjs format)
 * @returns Formatted date string
 */
export function formatDOB(
  date: string | Date | dayjs.Dayjs,
  format: string = "MM/DD/YYYY",
): string {
  return formatDate(date, format, true);
}

/**
 * Converts a date to the user's timezone
 * @param date The date to convert
 * @returns Date object in user's timezone
 */
export function toUserTimezone(date: string | Date | dayjs.Dayjs): dayjs.Dayjs {
  if (!date) {
    return dayjs();
  }

  const userTimezone = getUserTimezone();
  return dayjs(date).tz(userTimezone);
}
